/**
 * Store para el estado de autenticación usando Signals de Angular
 */
import { Injectable, computed, effect, signal } from '@angular/core';
import { IAuthState, IAuthTokens, IUser } from '../models/auth.types';
import { 
  clearAuthData, 
  getAuthTokens, 
  getUserData, 
  saveAuthTokens, 
  saveUserData 
} from '../utils/session-storage.util';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthStore {
  // Estado inicial
  private initialState: IAuthState = {
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: false,
    error: null
  };

  // Señal principal para el estado de autenticación
  private state = signal<IAuthState>({...this.initialState});

  // Señales computadas para acceder a partes específicas del estado
  readonly user = computed(() => this.state().user);
  readonly tokens = computed(() => this.state().tokens);
  readonly isAuthenticated = computed(() => this.state().isAuthenticated);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly error = computed(() => this.state().error);

  // URL a la que redirigir después del login
  private redirectUrl: string | null = null;

  constructor(private router: Router) {
    // Inicializar el estado desde el almacenamiento local
    this.checkAuthStatus();

    // Efecto para guardar cambios en el almacenamiento local
    effect(() => {
      const currentState = this.state();
      if (currentState.isAuthenticated && currentState.user && currentState.tokens) {
        saveUserData(currentState.user);
        saveAuthTokens(currentState.tokens);
      }
    });
  }

  /**
   * Verifica el estado de autenticación al iniciar
   */
  private checkAuthStatus(): void {
    const tokens = getAuthTokens();
    const user = getUserData();
    
    if (tokens && user) {
      this.state.update(state => ({
        ...state,
        tokens,
        user,
        isAuthenticated: true
      }));
    }
  }

  /**
   * Establece el usuario y tokens después de un login exitoso
   */
  setAuthenticated(user: IUser, tokens: IAuthTokens): void {
    this.state.update(state => ({
      ...state,
      user,
      tokens,
      isAuthenticated: true,
      isLoading: false,
      error: null
    }));
  }

  /**
   * Actualiza solo los tokens (útil para refresh token)
   */
  updateTokens(tokens: IAuthTokens): void {
    this.state.update(state => ({
      ...state,
      tokens
    }));
  }

  /**
   * Actualiza solo la información del usuario
   */
  updateUser(user: IUser): void {
    this.state.update(state => ({
      ...state,
      user
    }));
  }

  /**
   * Establece el estado de carga
   */
  setLoading(isLoading: boolean): void {
    this.state.update(state => ({
      ...state,
      isLoading,
      error: isLoading ? null : state.error
    }));
  }

  /**
   * Establece un mensaje de error
   */
  setError(error: string | null): void {
    this.state.update(state => ({
      ...state,
      error,
      isLoading: false
    }));
  }

  /**
   * Cierra la sesión del usuario
   */
  logout(): void {
    clearAuthData();
    this.state.set({...this.initialState});
    this.router.navigate(['/auth/login']);
  }

  /**
   * Establece la URL a la que redirigir después del login
   */
  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  /**
   * Navega a la URL guardada después del login o a la ruta por defecto
   */
  navigateAfterLogin(): void {
    const url = this.redirectUrl || '/';
    this.redirectUrl = null;
    this.router.navigateByUrl(url);
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    return this.user()?.roles?.includes(role) || false;
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    return this.user()?.permissions?.includes(permission) || false;
  }
}
